import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore'; 
import { db } from '../firebase';
import { MapPin, Star, Phone, Clock, Users, Calendar, Check, X, ChevronLeft, ChevronRight, Navigation } from 'lucide-react';
import ReviewCard from './ReviewCard';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const StadiumDetail = ({ stadiumId, onSelectSport }) => {
  const { t, i18n } = useTranslation();
  const [stadium, setStadium] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);

  useEffect(() => {
    const loadStadiumData = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, 'stadiums', stadiumId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setStadium({ id: docSnap.id, ...docSnap.data() });
        }
      } catch (error) {
        console.error('Error loading stadium:', error);
      }
      setLoading(false);
    };

    const loadReviews = async () => {
      setLoadingReviews(true);
      try {
        // --- FIX 1: REMOVED THE 'orderBy' TO PREVENT INDEX ERROR ---
        const reviewsQuery = query(
          collection(db, 'reviews'),
          where('stadiumId', '==', stadiumId)
        );
        const querySnapshot = await getDocs(reviewsQuery);
        const fetchedReviews = [];
        querySnapshot.forEach((doc) => {
          fetchedReviews.push({ id: doc.id, ...doc.data() });
        });

        // Sort in JavaScript instead of in the query
        fetchedReviews.sort((a, b) => b.createdAt.localeCompare(a.createdAt));

        setReviews(fetchedReviews);
      } catch (error) {
        console.error('Error loading reviews:', error);
      }
      setLoadingReviews(false);
    };

    loadStadiumData();
    loadReviews();
  }, [stadiumId]);

  const openLightbox = (index) => {
    setSelectedImageIndex(index);
    setLightboxOpen(true);
  };
  const closeLightbox = () => setLightboxOpen(false);
  const nextImage = (e) => {
    e.stopPropagation();
    if (stadium && stadium.photos) {
      setSelectedImageIndex((prevIndex) => (prevIndex + 1) % stadium.photos.length);
    }
  };
  const prevImage = (e) => {
    e.stopPropagation();
    if (stadium && stadium.photos) {
      setSelectedImageIndex((prevIndex) => (prevIndex - 1 + stadium.photos.length) % stadium.photos.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-100 dark:bg-neutral-900 flex items-center justify-center">
        <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!stadium) {
    return (
      <div className="min-h-screen bg-neutral-100 dark:bg-neutral-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-neutral-800 dark:text-white">Stadium not found</h2>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br from-amber-50 via-white to-yellow-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 transition-colors ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      

      {/* Stadium Hero Image - Enhanced with Luxury Gradient */}
      <div className="relative h-[500px] overflow-hidden">
        <button
          type="button"
          className="w-full h-full contents"
          onClick={() => openLightbox(0)}
        >
          <img
            src={stadium.photos[0]}
            alt={stadium.name}
            className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-700"
          />
        </button>
        {/* Luxury Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-transparent to-accent/20 pointer-events-none"></div>
        
        {/* Floating Orbs for Depth */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary-400/10 rounded-full blur-3xl animate-float pointer-events-none"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-accent-400/10 rounded-full blur-3xl animate-float pointer-events-none" style={{ animationDelay: '2s' }}></div>
        
        <div className="absolute bottom-8 left-0 right-0 pointer-events-none">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 drop-shadow-2xl animate-fade-in-up">
              {stadium.name}
            </h1>
            <div className="flex flex-wrap items-center gap-4 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              {/* Glass Badge - Location */}
              <div className="flex items-center gap-2 bg-white/95 dark:bg-neutral-800/95 backdrop-blur-xl px-5 py-3 rounded-full shadow-luxury border border-white/20 text-neutral-800 dark:text-white pointer-events-auto hover:scale-105 transition-transform duration-300">
                <MapPin size={18} className="text-primary" />
                <span className="font-semibold">{stadium.location}</span>
              </div>
              {/* Glass Badge - Rating */}
              <div className="flex items-center gap-2 bg-white/95 dark:bg-neutral-800/95 backdrop-blur-xl px-5 py-3 rounded-full shadow-luxury border border-white/20 text-neutral-800 dark:text-white pointer-events-auto hover:scale-105 transition-transform duration-300">
                <Star size={18} className="fill-yellow-500 text-yellow-500" />
                <span className="font-bold">{stadium.rating}</span>
                <span className="opacity-70">({stadium.reviewCount})</span>
              </div>
              {/* Glass Badge - Phone */}
              <div className="flex items-center gap-2 bg-white/95 dark:bg-neutral-800/95 backdrop-blur-xl px-5 py-3 rounded-full shadow-luxury border border-white/20 text-neutral-800 dark:text-white pointer-events-auto hover:scale-105 transition-transform duration-300">
                <Phone size={18} className="text-primary" />
                <span className="font-semibold">{stadium.phone}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* About - Luxury Glass Card */}
            <div className="glass-card glass-card-hover rounded-2xl p-8 animate-fade-in-up">
              <h2 className="text-3xl font-bold text-neutral-800 dark:text-white mb-4 flex items-center gap-3">
                <div className="w-2 h-8 bg-gradient-to-b from-primary to-accent rounded-full"></div>
                {t('detailpage.about')}
              </h2>
              <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed text-lg">{stadium.description}</p>
            </div>

            {/* Amenities - Luxury Glass Card */}
            <div className="glass-card glass-card-hover rounded-2xl p-8 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <h2 className="text-3xl font-bold text-neutral-800 dark:text-white mb-6 flex items-center gap-3">
                <div className="w-2 h-8 bg-gradient-to-b from-primary to-accent rounded-full"></div>
                {t('detailpage.amenities')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {stadium.amenities.map((amenity, index) => (
                  <div key={index} className="flex items-center gap-3 group hover:scale-105 transition-transform duration-300">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 dark:from-primary-500 dark:to-primary-700 rounded-xl flex items-center justify-center flex-shrink-0 shadow-emerald group-hover:shadow-luxury transition-shadow duration-300">
                      <Check size={20} className="text-white" />
                    </div>
                    <span className="text-neutral-700 dark:text-neutral-300 font-semibold">{amenity}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sports Offered - Premium Cards */}
            <div className="glass-card glass-card-hover rounded-2xl p-8 animate-fade-in-up" style={{ animationDelay: '0.2s' }} data-sports-section>
              <h2 className="text-3xl font-bold text-neutral-800 dark:text-white mb-6 flex items-center gap-3">
                <div className="w-2 h-8 bg-gradient-to-b from-primary to-accent rounded-full"></div>
                {t('detailpage.availableSports')}
              </h2>
              <div className="space-y-4">
                {stadium.sports.map((sport, index) => (
                  <div
                    key={sport.id}
                    className="group relative overflow-hidden p-6 rounded-2xl border-2 border-neutral-200 dark:border-neutral-700 hover:border-primary-light hover:shadow-luxury transition-all duration-300 bg-white/50 dark:bg-neutral-900/50 backdrop-blur-sm hover:bg-gradient-to-br hover:from-primary/5 hover:to-accent/5 dark:hover:from-primary/10 dark:hover:to-accent/10 animate-fade-in-up"
                    style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                  >
                    {/* Shimmer effect on hover */}
                    <div className="absolute inset-0 shimmer opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    <div className="relative z-10 flex flex-col md:flex-row justify-between gap-6">
                      {/* Left Side: Details */}
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-neutral-800 dark:text-white mb-4 group-hover:text-primary transition-colors duration-300">
                          {sport.name}
                        </h3>
                        <div className="space-y-3 text-neutral-600 dark:text-neutral-400">
                          <div className="flex items-center gap-3 group/item hover:text-primary transition-colors">
                            <div className="w-9 h-9 bg-primary/10 dark:bg-primary/20 rounded-lg flex items-center justify-center">
                              <Users size={18} className="text-primary" />
                            </div>
                            <span className="font-medium">{t('detailpage.players', { count: sport.capacity })}</span>
                          </div>
                          <div className="flex items-center gap-3 group/item hover:text-primary transition-colors">
                            <div className="w-9 h-9 bg-primary/10 dark:bg-primary/20 rounded-lg flex items-center justify-center">
                              <Clock size={18} className="text-primary" />
                            </div>
                            <span className="font-medium">{t('detailpage.minutes', { count: sport.duration })}</span>
                          </div>
                          <div className="flex items-center gap-3 group/item hover:text-primary transition-colors">
                            <div className="w-9 h-9 bg-primary/10 dark:bg-primary/20 rounded-lg flex items-center justify-center">
                              <Calendar size={18} className="text-primary" />
                            </div>
                            <span className="font-medium">{sport.openTime} - {sport.closeTime}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Right Side: Price & Button */}
                      <div className="text-left md:text-right flex-shrink-0 flex flex-col justify-between">
                        <div className="mb-4">
                          <div className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">{t('detailpage.perSlot')}</div>
                          <div className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                            ${sport.pricePerSlot}
                          </div>
                        </div>
                        <button 
                          onClick={() => onSelectSport(stadiumId, sport)}
                          className="luxury-button w-full md:w-auto bg-gradient-to-r from-primary to-primary-600 hover:from-primary-600 hover:to-accent text-white px-8 py-4 rounded-xl font-bold transition-all duration-300 shadow-emerald hover:shadow-luxury hover:scale-105"
                        >
                          {t('detailpage.bookNow')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Location Map Section - Luxury Glass Card */}
            {stadium.coordinates && (
              <div className="glass-card glass-card-hover rounded-2xl p-8 animate-fade-in-up" style={{ animationDelay: '0.25s' }}>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-3xl font-bold text-neutral-800 dark:text-white flex items-center gap-3">
                    <div className="w-2 h-8 bg-gradient-to-b from-primary to-accent rounded-full"></div>
                    {t('detailpage.location')}
                  </h2>
                  <a 
                    href={`https://www.google.com/maps/dir/?api=1&destination=${stadium.coordinates.lat},${stadium.coordinates.lng}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-primary hover:text-accent font-semibold transition-colors"
                  >
                    <Navigation size={18} />
                    <span>{t('detailpage.getDirections')}</span>
                  </a>
                </div>
                
                <div className="h-[400px] w-full rounded-xl overflow-hidden shadow-inner border border-neutral-200 dark:border-neutral-700 relative z-0">
                  <MapContainer 
                    center={[stadium.coordinates.lat, stadium.coordinates.lng]} 
                    zoom={15} 
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={false}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[stadium.coordinates.lat, stadium.coordinates.lng]}>
                      <Popup>
                        <div className="font-semibold text-center">
                          {stadium.name}
                          <br />
                          {stadium.location}
                        </div>
                      </Popup>
                    </Marker>
                  </MapContainer>
                </div>
                <div className="mt-4 flex items-center gap-2 text-neutral-600 dark:text-neutral-400">
                  <MapPin size={18} className="text-primary" />
                  <span>{stadium.location}, {stadium.area}, {stadium.city}</span>
                </div>
              </div>
            )}

            {/* Reviews Section - Luxury Glass Card */}
            <div className="glass-card glass-card-hover rounded-2xl p-8 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <h2 className="text-3xl font-bold text-neutral-800 dark:text-white mb-6 flex items-center gap-3">
                <div className="w-2 h-8 bg-gradient-to-b from-primary to-accent rounded-full"></div>
                {t('detailpage.reviews')} ({reviews.length})
              </h2>
              {loadingReviews ? (
                <div className="text-center py-12">
                  <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-neutral-500 dark:text-neutral-400 font-semibold">{t('detailpage.loadingReviews')}</p>
                </div>
              ) : reviews.length > 0 ? (
                <div className="space-y-4">
                  {reviews.map(review => (
                    <div key={review.id} className="p-4 rounded-xl bg-neutral-50 dark:bg-neutral-800/50 hover:bg-neutral-100 dark:hover:bg-neutral-700/50 transition-colors duration-300">
                      <ReviewCard review={review} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-neutral-100 dark:bg-neutral-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Star size={32} className="text-neutral-400" />
                  </div>
                  <p className="text-neutral-500 dark:text-neutral-400 text-lg">{t('detailpage.noReviews')}</p>
                </div>
              )}
            </div>
            
          </div>

          {/* Sidebar - Luxury Glass Cards */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 space-y-6">
              {/* Quick Info Card */}
              <div className="glass-card rounded-2xl p-6 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                <h3 className="text-2xl font-bold text-neutral-800 dark:text-white mb-6 flex items-center gap-3">
                  <div className="w-1.5 h-6 bg-gradient-to-b from-primary to-accent rounded-full"></div>
                  {t('detailpage.quickInfo')}
                </h3>
                <div className="space-y-4 mb-6">
                  <div className="flex items-center justify-between py-4 border-b-2 border-neutral-200 dark:border-neutral-700 group hover:border-primary dark:hover:border-primary transition-colors">
                    <span className="text-neutral-600 dark:text-neutral-400 font-semibold">{t('detailpage.startingPrice')}</span>
                    <span className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      ${Math.min(...stadium.sports.map(s => s.pricePerSlot))}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-4 border-b-2 border-neutral-200 dark:border-neutral-700 group hover:border-primary dark:hover:border-primary transition-colors">
                    <span className="text-neutral-600 dark:text-neutral-400 font-semibold">{t('detailpage.sportsAvailable')}</span>
                    <span className="text-2xl font-bold text-neutral-800 dark:text-white">{stadium.sports.length}</span>
                  </div>
                  <div className="flex items-center justify-between py-4">
                    <span className="text-neutral-600 dark:text-neutral-400 font-semibold">{t('detailpage.rating')}</span>
                    <div className="flex items-center gap-2">
                      <Star size={20} className="fill-yellow-500 text-yellow-500" />
                      <span className="text-2xl font-bold text-neutral-800 dark:text-white">{stadium.rating}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    document.querySelector('[data-sports-section]')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="luxury-button w-full bg-gradient-to-r from-primary to-primary-600 hover:from-primary-600 hover:to-accent text-white py-4 rounded-xl font-bold transition-all duration-300 shadow-emerald hover:shadow-luxury hover:scale-105"
                >
                  {t('detailpage.chooseSport')}
                </button>
              </div>
              
              {/* Gallery Card */}
              <div className="glass-card rounded-2xl p-6 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
                <h3 className="text-2xl font-bold text-neutral-800 dark:text-white mb-6 flex items-center gap-3">
                  <div className="w-1.5 h-6 bg-gradient-to-b from-primary to-accent rounded-full"></div>
                  {t('detailpage.gallery')}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {stadium.photos.slice(1).map((photo, index) => (
                    <div key={index} className="relative group overflow-hidden rounded-xl cursor-pointer">
                      <img
                        src={photo}
                        alt={`${stadium.name} ${index + 2}`}
                        className="w-full h-32 object-cover group-hover:scale-110 transition-transform duration-500"
                        onClick={() => openLightbox(index + 1)}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                          <span className="text-white text-2xl">+</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Modal - Luxury Enhanced */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center transition-opacity duration-300 animate-fade-in"
          onClick={closeLightbox}
        >
          {/* Close Button - Luxury Glass */}
          <button 
            className="absolute top-6 right-6 p-3 bg-white/10 backdrop-blur-xl rounded-full text-white/90 hover:text-white hover:bg-white/20 transition-all duration-300 border border-white/20 shadow-luxury z-10"
            onClick={closeLightbox}
          >
            <X size={32} />
          </button>
          
          {/* Navigation Buttons - Luxury Glass */}
          <button 
            className="absolute left-6 top-1/2 -translate-y-1/2 p-4 bg-white/10 backdrop-blur-xl rounded-full text-white/90 hover:text-white hover:bg-white/20 transition-all duration-300 border border-white/20 shadow-luxury hover:scale-110"
            onClick={prevImage}
          >
            <ChevronLeft size={40} />
          </button>
          <button 
            className="absolute right-6 top-1/2 -translate-y-1/2 p-4 bg-white/10 backdrop-blur-xl rounded-full text-white/90 hover:text-white hover:bg-white/20 transition-all duration-300 border border-white/20 shadow-luxury hover:scale-110"
            onClick={nextImage}
          >
            <ChevronRight size={40} />
          </button>
          
          {/* Image Container with Luxury Frame */}
          <div className="max-w-6xl max-h-[90vh] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="relative overflow-hidden rounded-2xl shadow-2xl border-2 border-white/20">
              <img 
                src={stadium.photos[selectedImageIndex]} 
                alt="Stadium"
                className="w-full h-full object-contain"
              />
              {/* Image Counter Badge */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/90 dark:bg-neutral-800/90 backdrop-blur-xl px-6 py-3 rounded-full text-neutral-800 dark:text-white font-bold shadow-luxury border border-white/20">
                {selectedImageIndex + 1} / {stadium.photos.length}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- FIX 3: REMOVED THE STRAY <style jsx> TAG --- */}
    </div>
  );
};

export default StadiumDetail;